import React from "react";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/pro-regular-svg-icons";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  isSchoolUser,
  isUserExpired,
  isUserSignedUp,
  nonGuestProfiles,
} from "./api";
import {
  Account,
  AccountContext,
  MAX_PROFILES_PER_USER,
  addProfilePath,
  editProfilePath,
  isProfileManagementUnlocked,
} from "./context/AccountContext";
import {
  AnalyticsContext,
  MenuAddProfileButtonTapEvent,
  MenuProfileSettingsButtonTapEvent,
  MenuSelectProfileButtonTapEvent,
} from "./context/AnalyticsContext";
import { SingleLineText } from "./CommonStyles";
import {
  POPUP_MENU_DIVIDER_SX,
  POPUP_MENU_ITEM_SX,
  PopupMenu,
  PopupMenuCheck,
} from "./PopupMenu";

export const canSwitchProfile = (account: Account) =>
  isUserSignedUp(account.user) &&
  !isSchoolUser(account.user) &&
  account.hasNonGuestProfile() &&
  account.profile!.first_name.trim() !== "";

export const canSwitchBetweenProfiles = (account: Account) =>
  canSwitchProfile(account) && nonGuestProfiles(account.user).length > 1;

const VARIANT_SX = {
  popup: {
    item: POPUP_MENU_ITEM_SX,
    divider: POPUP_MENU_DIVIDER_SX,
  },
  inline: {
    item: { paddingLeft: "32px" },
    divider: { marginLeft: "32px", marginRight: "16px" },
  },
};

export const ProfileSwitcherItems: React.FC<{
  account: Account;
  variant: keyof typeof VARIANT_SX;
  showProfileManagement?: boolean;
  onDone: () => void;
}> = ({ account, variant, showProfileManagement = true, onDone }) => {
  const history = useHistory();
  const { updateUser } = React.useContext(AccountContext);
  const { logEvent } = React.useContext(AnalyticsContext);
  const { t } = useTranslation();

  const currentProfile = account.profile!;
  const profiles = nonGuestProfiles(account.user);
  const sx = VARIANT_SX[variant];

  return (
    <>
      {profiles.map((profile) => (
        <MenuItem
          key={profile.id}
          sx={sx.item}
          onClick={() => {
            logEvent(new MenuSelectProfileButtonTapEvent(profile.id));
            onDone();
            if (profile.id === currentProfile.id) {
              return;
            }
            if (isUserExpired(account.user)) {
              history.push("/account/upgrade/");
            } else {
              updateUser(account.user, false, profile.id, false);
            }
          }}
        >
          {profile.id === currentProfile.id && <PopupMenuCheck />}
          <SingleLineText>{profile.first_name}</SingleLineText>
        </MenuItem>
      ))}
      {showProfileManagement && (
        <>
          <Divider sx={sx.divider} />
          {profiles.length < MAX_PROFILES_PER_USER && (
            <MenuItem
              sx={sx.item}
              onClick={() => {
                logEvent(new MenuAddProfileButtonTapEvent());
                onDone();
                history.push(addProfilePath(account.user));
              }}
            >
              {t("navigation_create_new_profile")}
              {!isProfileManagementUnlocked(account.user) && (
                <FontAwesomeIcon
                  icon={faLock}
                  style={{ height: 12, marginLeft: 8 }}
                />
              )}
            </MenuItem>
          )}
          <MenuItem
            sx={sx.item}
            onClick={() => {
              logEvent(new MenuProfileSettingsButtonTapEvent());
              onDone();
              history.push(editProfilePath(currentProfile.id));
            }}
          >
            <SingleLineText>
              {t("navigation_edit_named_profile", {
                name: currentProfile.first_name.trim(),
              })}
            </SingleLineText>
          </MenuItem>
        </>
      )}
    </>
  );
};

export const ProfileSwitcherMenu: React.FC<{
  account: Account;
  anchorEl: HTMLElement | null;
  open: boolean;
  showProfileManagement?: boolean;
  onClose: () => void;
}> = ({ account, anchorEl, open, showProfileManagement = true, onClose }) => (
  <PopupMenu
    open={open}
    onClose={onClose}
    anchorEl={anchorEl}
    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    transformOrigin={{ vertical: "top", horizontal: "left" }}
    paperSx={{ marginLeft: "10px" }}
  >
    <ProfileSwitcherItems
      account={account}
      variant="popup"
      showProfileManagement={showProfileManagement}
      onDone={onClose}
    />
  </PopupMenu>
);
